/**
 * Client-side helpers for the search bar. The heavy lifting (fuzzy matching, intent
 * detection) happens on the server; these keep the UI honest about what it is showing.
 */

const LOCATION_WORDS = ["in", "near", "around", "at"];

/** Splits "korean in fort lee" into its "what" and "where" halves for the UI hint. */
export function splitQuery(raw) {
  const text = String(raw ?? "").trim().replace(/\s+/g, " ");
  if (!text) return { what: "", where: "" };
  const lower = text.toLowerCase();
  for (const word of LOCATION_WORDS) {
    const marker = ` ${word} `;
    const index = lower.lastIndexOf(marker);
    if (index > 0) {
      return {
        what: text.slice(0, index).trim(),
        where: text.slice(index + marker.length).trim(),
      };
    }
  }
  return { what: text, where: "" };
}

/**
 * Builds the query string for /api/restaurants from the current UI state.
 *
 * Cuisine and town are **sets**: the checkbox groups allow several alternatives, and the checked
 * ones are OR-ed, so each travels as its own repeated parameter. Price and rating are ceilings —
 * the caller reduces its checked boxes to the loosest one, which is exactly what OR-ing two
 * "at most $$" options means.
 */
export function buildSearchParams({ q = "", page = 1, pageSize = 24, sort, cuisines, cities, minRating, maxPrice } = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (page && page !== 1) params.set("page", String(page));
  if (pageSize && pageSize !== 24) params.set("pageSize", String(pageSize));
  if (sort) params.set("sort", sort);
  for (const value of cuisines ?? []) if (value && String(value).trim()) params.append("cuisine", value);
  for (const value of cities ?? []) if (value && String(value).trim()) params.append("city", value);
  if (minRating) params.set("minRating", String(minRating));
  if (maxPrice) params.set("maxPrice", String(maxPrice));
  return params;
}

/** The ticked boxes for a threshold facet. */
function thresholdBoxes(params, param, legacyParam, levels, direction) {
  const explicit = (params?.getAll(param) ?? []).filter(Boolean);
  if (explicit.length) return explicit;
  const legacy = params?.get(legacyParam);
  if (!legacy) return [];
  return direction === "asc" ? levels.slice(0, Number(legacy)).map(String) : [String(legacy)];
}

/**
 * The applied filters as the box values they came from, read from the URL.
 *
 * One definition for both readers — the chips in the search box and the checkbox lists that edit
 * them. Two would let the summary disagree with the boxes it is summarising.
 *
 * A link written before these were checkboxes carries a single ceiling (`maxPrice=2`); that is
 * read back as the boxes it always meant, because "$$ or less" was always "$ and $$".
 */
export function selectedFilters(params) {
  return {
    cuisine: (params?.getAll("cuisine") ?? []).filter(Boolean),
    city: (params?.getAll("city") ?? []).filter(Boolean),
    price: thresholdBoxes(params, "price", "maxPrice", [1, 2, 3, 4], "asc"),
    rating: thresholdBoxes(params, "rating", "minRating", [3, 4, 4.5], "desc"),
  };
}

/**
 * The filters to carry across when the text query changes.
 *
 * Typing a new term must not silently throw away the facets already chosen — they are shown beside
 * the text in the same box for exactly that reason. Sort comes along too: it is part of what the
 * reader is looking at, not part of the query.
 */
export function carriedFilters(params) {
  const selected = selectedFilters(params);
  const sort = params?.get("sort");
  return {
    ...(sort ? { sort } : {}),
    cuisine: selected.cuisine,
    city: selected.city,
    price: selected.price,
    rating: selected.rating,
  };
}

/** The same selection as chips: a facet label, and its values written the way a reader would. */
export function filterChips(params) {
  const selected = selectedFilters(params);
  return [
    { key: "cuisine", label: "Cuisine", values: selected.cuisine },
    { key: "city", label: "Town", values: selected.city },
    { key: "price", label: "Price", values: selected.price.map((level) => "$".repeat(Number(level))) },
    { key: "rating", label: "Rating", values: selected.rating.map((threshold) => `${threshold}+`) },
  ].filter((chip) => chip.values.length > 0);
}

/**
 * Summarises a facet's selected values for a chip: the first [max] of them, then an ellipsis and
 * how many are hidden. The full list belongs in the chip's tooltip — a chip that grew to fit
 * every value would stop being scannable, which is the whole reason for the chips.
 */
export function summarizeValues(values, max = 2) {
  const list = (values ?? []).filter((value) => value !== undefined && value !== null && value !== "");
  if (list.length <= max) return list.join(", ");
  return `${list.slice(0, max).join(", ")} …+${list.length - max}`;
}

/** Splits text into [{ text, match }] runs so the UI can highlight matched terms. */
export function highlightSegments(text, terms) {
  const value = String(text ?? "");
  const cleaned = (terms ?? [])
    .map((t) => String(t ?? "").trim())
    .filter((t) => t.length >= 2)
    .sort((a, b) => b.length - a.length);
  if (!value || cleaned.length === 0) return [{ text: value, match: false }];

  const pattern = new RegExp(`(${cleaned.map(escapeRegExp).join("|")})`, "ig");
  const segments = [];
  let cursor = 0;
  for (const match of value.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > cursor) segments.push({ text: value.slice(cursor, index), match: false });
    segments.push({ text: match[0], match: true });
    cursor = index + match[0].length;
  }
  if (cursor < value.length) segments.push({ text: value.slice(cursor), match: false });
  return segments.length ? segments : [{ text: value, match: false }];
}

export function escapeRegExp(text) {
  return String(text).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Keeps a bounded, de-duplicated most-recent-first list of search terms. */
export function rememberSearch(history, query, limit = 8) {
  const term = String(query ?? "").trim();
  if (!term) return history ?? [];
  const rest = (history ?? []).filter((item) => item.toLowerCase() !== term.toLowerCase());
  return [term, ...rest].slice(0, limit);
}
